import { Progress } from '@/components/ui/progress';
import { CheckCircle, Circle } from '@phosphor-icons/react';

const pickIconColor = ({ isComplete, isCurrent }) => {
  if (isComplete) return 'text-teal-600';
  if (isCurrent) return 'text-slate-900';
  return 'text-slate-300';
};

const pickLabelColor = ({ isComplete, isCurrent }) =>
  isComplete || isCurrent ? 'text-slate-900 font-semibold' : 'text-slate-400';

const StepDot = ({ idx, label, isComplete, isCurrent }) => {
  const Icon = isComplete ? CheckCircle : Circle;
  const iconColor = pickIconColor({ isComplete, isCurrent });
  const labelColor = pickLabelColor({ isComplete, isCurrent });

  return (
    <div className="flex flex-col items-center gap-1 min-w-0 flex-1" data-testid={`progress-step-${idx + 1}`}>
      <Icon size={20} weight={isComplete ? 'fill' : 'regular'} className={iconColor} />
      <span className={`text-[10px] sm:text-xs tracking-tight text-center truncate w-full ${labelColor}`}>
        {label}
      </span>
    </div>
  );
};

const SummaryRow = ({ step, totalSteps, progress, completedSteps, currentLabel }) => (
  <div className="flex items-baseline justify-between gap-2 flex-wrap mb-2">
    <div className="flex items-center gap-2 text-sm">
      <span className="text-slate-900 font-bold">Section {step} of {totalSteps}</span>
      {currentLabel && (
        <span className="hidden sm:inline-flex items-center px-2 py-0.5 rounded-full bg-teal-50 text-teal-700 text-[11px] font-semibold uppercase tracking-wider">
          {currentLabel}
        </span>
      )}
    </div>
    <div className="text-xs text-slate-500 flex items-center gap-3">
      <span><strong className="text-teal-700">{Math.round(progress)}%</strong> completed</span>
      <span>·</span>
      <span>{completedSteps.length} of {totalSteps} done</span>
    </div>
  </div>
);

const ProgressIndicator = ({ step, totalSteps, progress, completedSteps = [], stepLabels = [] }) => {
  const currentLabel = stepLabels[step - 1];
  return (
    <div data-testid="progress-indicator">
      <SummaryRow
        step={step}
        totalSteps={totalSteps}
        progress={progress}
        completedSteps={completedSteps}
        currentLabel={currentLabel}
      />
      <Progress value={progress} className="h-2 mb-4" />
      {stepLabels.length > 0 && (
        <div className="flex items-start justify-between gap-1 sm:gap-3">
          {stepLabels.map((label, idx) => (
            <StepDot
              key={label}
              idx={idx}
              label={label}
              isComplete={completedSteps.includes(idx + 1)}
              isCurrent={step === idx + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ProgressIndicator;
